import { useRemoteCacheInvalidation } from '@Hooks/useRemoteCacheInvalidation';
import { useDocumentsRepository } from '@Hooks/useRepository';
import type { ApiPrintOption, ApiPrintOptionPayload } from '@Model';
import { PrintOptionsRepository } from '@Repositories/Documents';
import { unwrapOr } from '@synqo/frontend-component-library';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState, type Dispatch, type SetStateAction } from 'react';

export interface PrintOptionsInfo {
    printOptions: ApiPrintOption[];
    isLoading: boolean;
    hasPrintOptions: boolean;
    currentPrintOption: ApiPrintOption | undefined;
    setCurrentPrintOption: Dispatch<SetStateAction<ApiPrintOption | undefined>>;
    refetch: () => Promise<ApiPrintOption[]>;
}

export function useGetAllPrintOptions() {
    const printOptionsApi = useDocumentsRepository(PrintOptionsRepository);

    const {
        data: queryResult,
        isLoading,
        isError,
    } = useQuery({
        queryFn: () => printOptionsApi.getPrintOptions(),
        queryKey: ['documents', 'options'],
    });

    const printOptions = unwrapOr(queryResult, []);

    return { printOptions, isLoading, isError };
}

export function usePrintOptions(category: string, primaryId: string, enabled: boolean): PrintOptionsInfo {
    const printOptionsApi = useDocumentsRepository(PrintOptionsRepository);
    const [currentPrintOption, setCurrentPrintOption] = useState<ApiPrintOption>();

    const payload: ApiPrintOptionPayload = {
        primary_id: primaryId ?? '',
    };

    const {
        data: queryResult,
        refetch: refetchQuery,
        isLoading,
    } = useQuery({
        queryFn: () => printOptionsApi.getPrintOptionsInCategory(payload, category),
        queryKey: ['documents', 'options', category, primaryId],
        enabled,
    });

    const refetch = useCallback(
        async () => (await refetchQuery({ cancelRefetch: false })).data?.data ?? [],
        [refetchQuery],
    );

    // Refetch whenever the BE broadcasts an attachment-cache-invalidation signal, so a newly
    // uploaded/deleted attachment shows up in the print option list without a manual page refresh.
    useRemoteCacheInvalidation('attachments', refetchQuery);

    const printOptions = queryResult?.data ?? [];
    const hasPrintOptions = printOptions.length > 0;

    return {
        printOptions,
        isLoading,
        hasPrintOptions,
        currentPrintOption,
        setCurrentPrintOption,
        refetch,
    };
}
